// Page "Dashboard Hsasoft" (Lot 8.6.3) — vue cross-tenant du parc HInvoice.
//
// Réservée aux admins Hsasoft (allow-list email côté backend `HSASOFT_ADMIN_EMAILS`).
// La garde se fait côté API : si l'email courant n'est pas dans la liste, on
// reçoit 403 ; si la liste est vide (feature désactivée), on reçoit 404. Dans
// les deux cas, on rend un message d'erreur explicite — sans rien dévoiler du
// reste (cf design Lot 8.6.1).
//
// Côté composant, on ne re-vérifie PAS l'email côté front : ce serait un
// dédoublement fragile (la liste vit dans l'env serveur) et on veut que la
// vérité reste au backend. La page est donc atteignable par n'importe quel
// utilisateur authentifié — elle ne lui montrera juste rien.
//
// Contenu :
// - 4 KPI cards : MRR, subscriptions actives, churn 30j, BI impayées
// - Tableau "Derniers tenants" (10) : nom / plan / statut sub / trial / créé
// - Tableau "BI impayées" (10) : référence / tenant / montant / due_date / retard

import { useEffect, useState } from "react";

import {
  adminApi,
  type AdminOverview,
  type AdminTenantRow,
  type AdminUnpaidBiRow,
} from "../api/admin";
import type { SubscriptionStatus } from "../api/billing";
import { ApiError } from "../api/client";
import { AppShell } from "../components/AppShell";

// ---- Helpers de formatage ----------------------------------------------

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatAmount(value: string, currency: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatDateFR(iso: string): string {
  // Accepte ISO date (YYYY-MM-DD) ou ISO datetime — suffixe Z pour rester UTC.
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateTimeFR(iso: string): string {
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const SUB_STATUS_LABEL: Record<SubscriptionStatus, string> = {
  incomplete: "À finaliser",
  active: "Active",
  past_due: "Paiement en retard",
  canceled: "Annulée",
};

const SUB_STATUS_BADGE: Record<SubscriptionStatus, string> = {
  incomplete: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  past_due: "bg-red-100 text-red-800",
  canceled: "bg-slate-200 text-slate-700",
};

// ---- Page ---------------------------------------------------------------

export function AdminOverviewPage() {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const overview = await adminApi.overview();
        if (!cancelled) setData(overview);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setErrorStatus(err.status);
          setErrorMessage(err.detail);
        } else {
          setErrorMessage(
            err instanceof Error ? err.message : "Erreur de chargement",
          );
        }
        setData(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppShell>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          Dashboard Hsasoft
        </h1>
        <p className="text-sm text-slate-500">
          Vue d'ensemble du parc HInvoice — tenants, MRR, churn et factures
          impayées. Réservé aux administrateurs Hsasoft.
        </p>
      </header>

      {errorStatus === 404 && (
        <div
          role="alert"
          className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-md text-sm"
        >
          Tableau de bord indisponible. Contactez l'éditeur si vous pensez
          recevoir cette erreur par erreur.
        </div>
      )}

      {errorStatus === 403 && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md text-sm"
        >
          Accès refusé : ce tableau de bord est réservé à l'équipe Hsasoft.
        </div>
      )}

      {errorMessage && errorStatus !== 404 && errorStatus !== 403 && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md text-sm"
        >
          {errorMessage}
        </div>
      )}

      {data === null && !errorMessage && (
        <p className="text-sm text-slate-500">Chargement…</p>
      )}

      {data && <OverviewBody data={data} />}
    </AppShell>
  );
}

// ---- Corps de la page ---------------------------------------------------

function OverviewBody({ data }: { data: AdminOverview }) {
  return (
    <div className="space-y-6">
      <p className="text-xs text-slate-400">
        Données arrêtées au {formatDateTimeFR(data.generated_at)}
      </p>

      {/* KPI cards */}
      <section
        aria-label="Indicateurs clés"
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <KpiCard
          label="MRR"
          value={formatCents(data.mrr_cents, data.mrr_currency)}
          hint={`${data.active_subscriptions} sub actives`}
        />
        <KpiCard
          label="Tenants"
          value={String(data.tenants.total)}
          hint={`${data.tenants.active} actifs · ${data.tenants.trial} en essai`}
        />
        <KpiCard
          label="Churn 30j"
          value={String(data.churn_30d)}
          hint="annulations effectives"
          tone={data.churn_30d > 0 ? "warning" : "neutral"}
        />
        <KpiCard
          label="BI impayées"
          value={String(data.unpaid_bi.count)}
          hint={formatAmount(
            data.unpaid_bi.total_amount_ttc,
            data.unpaid_bi.currency,
          )}
          tone={data.unpaid_bi.count > 0 ? "warning" : "neutral"}
        />
      </section>

      {/* Répartition tenants */}
      <section className="bg-white border border-slate-200 rounded-md p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-3">
          Répartition des tenants
        </h2>
        <dl className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
          <StatPill label="Actifs" value={data.tenants.active} />
          <StatPill label="En grâce" value={data.tenants.past_due} />
          <StatPill label="En essai" value={data.tenants.trial} />
          <StatPill label="Annulés" value={data.tenants.canceled} />
          <StatPill label="Sans sub" value={data.tenants.no_subscription} />
        </dl>
      </section>

      {/* Tableau tenants */}
      <section className="bg-white border border-slate-200 rounded-md">
        <header className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            Derniers tenants créés
          </h2>
          <span className="text-xs text-slate-500">
            {data.latest_tenants.length} affichés
          </span>
        </header>
        {data.latest_tenants.length === 0 ? (
          <p className="p-6 text-sm text-slate-500 italic">
            Aucun tenant à afficher.
          </p>
        ) : (
          <TenantsTable rows={data.latest_tenants} />
        )}
      </section>

      {/* Tableau BI impayées */}
      <section className="bg-white border border-slate-200 rounded-md">
        <header className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            Factures Hsasoft impayées
          </h2>
          <span className="text-xs text-slate-500">
            {data.latest_unpaid_bi.length} affichées
          </span>
        </header>
        {data.latest_unpaid_bi.length === 0 ? (
          <p className="p-6 text-sm text-slate-500 italic">
            Aucune facture impayée — tout est à jour.
          </p>
        ) : (
          <UnpaidBiTable rows={data.latest_unpaid_bi} />
        )}
      </section>
    </div>
  );
}

// ---- Sous-composants ---------------------------------------------------

function KpiCard({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "neutral" | "warning";
}) {
  const valueClass =
    tone === "warning" ? "text-red-700" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${valueClass}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function StatPill({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd className="text-lg font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

function TenantsTable({ rows }: { rows: AdminTenantRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Nom</th>
            <th className="text-left px-4 py-2 font-medium">Slug</th>
            <th className="text-left px-4 py-2 font-medium">Plan</th>
            <th className="text-left px-4 py-2 font-medium">Sub</th>
            <th className="text-left px-4 py-2 font-medium">Essai jusqu'à</th>
            <th className="text-left px-4 py-2 font-medium">Créé le</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr
              key={t.id}
              className="border-t border-slate-100 hover:bg-slate-50"
            >
              <td className="px-4 py-2 text-slate-900">{t.name}</td>
              <td className="px-4 py-2 font-mono text-xs text-slate-600">
                {t.slug}
              </td>
              <td className="px-4 py-2 text-slate-700">
                {t.plan_slug ?? "—"}
              </td>
              <td className="px-4 py-2">
                {t.subscription_status ? (
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${SUB_STATUS_BADGE[t.subscription_status]}`}
                  >
                    {SUB_STATUS_LABEL[t.subscription_status]}
                  </span>
                ) : (
                  <span className="text-xs text-slate-400">—</span>
                )}
              </td>
              <td className="px-4 py-2 text-slate-600">
                {t.trial_ends_at ? formatDateFR(t.trial_ends_at) : "—"}
              </td>
              <td className="px-4 py-2 text-slate-600">
                {formatDateFR(t.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UnpaidBiTable({ rows }: { rows: AdminUnpaidBiRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Référence</th>
            <th className="text-left px-4 py-2 font-medium">Tenant</th>
            <th className="text-right px-4 py-2 font-medium">Montant</th>
            <th className="text-left px-4 py-2 font-medium">Échéance</th>
            <th className="text-left px-4 py-2 font-medium">Retard</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((bi) => (
            <tr
              key={bi.id}
              className="border-t border-slate-100 hover:bg-slate-50"
            >
              <td className="px-4 py-2 font-mono text-xs text-slate-700">
                {bi.reference}
              </td>
              <td className="px-4 py-2 text-slate-900">{bi.tenant_name}</td>
              <td className="px-4 py-2 text-right font-medium text-slate-900">
                {formatAmount(bi.amount_ttc, bi.currency)}
              </td>
              <td className="px-4 py-2 text-slate-600">
                {formatDateFR(bi.due_date)}
              </td>
              <td className="px-4 py-2">
                <OverdueBadge days={bi.days_overdue} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OverdueBadge({ days }: { days: number }) {
  if (days <= 0) {
    // Échéance future ou aujourd'hui → pas (encore) en retard
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
        {days === 0 ? "aujourd'hui" : `dans ${-days} j`}
      </span>
    );
  }
  const tone =
    days > 14
      ? "bg-red-100 text-red-800"
      : days > 5
        ? "bg-amber-100 text-amber-800"
        : "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {days} j
    </span>
  );
}
